import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FilePlus2, Folder, FolderOpen, ArrowUpDown } from "lucide-react";
import { api, eur, dt, TIPI, errMsg } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Filtri, EMPTY_FILTRI, filtriParams, RowMenu, RowIcons, BulkBar, Pager } from "@/components/InvoiceListParts";
import { useAuth, canWrite } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { ImportXmlDialog } from "@/components/ImportXmlDialog";
import { RicevuteZipDialog } from "@/components/RicevuteZipDialog";
import { ConservazioneBadge } from "@/components/ConservazioneBadge";
import { PrintButton } from "@/components/PrintButton";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CUR = String(new Date().getFullYear());
const SENT = "INVIATA,CONSEGNATA,MANCATA_CONSEGNA";
const VISTE = [
  ["da_inviare", "Da inviare", "emessa", { stati: "BOZZA,SCARTATA", imported: false }],
  ["inviati", "Inviati", "emessa", { stati: SENT, imported: false, principali: true, year: CUR, archiviata: false }],
  ["storico_inviati", "Storico inviati", "emessa", { stati: SENT, imported: false, principali: true }],
  ["altri", "Altri documenti", "emessa", { stati: SENT, imported: false, principali: false }],
  ["conservazione", "Sola conservazione", "emessa", { imported: true }],
  ["ricevuti", "Ricevuti", "ricevuta", { year: CUR, archiviata: false }],
  ["storico_ricevuti", "Storico ricevuti", "ricevuta", {}],
];

const Tabs = ({ vista, onPick }) => (
  <div className="mb-4 flex gap-1 overflow-x-auto border-b border-slate-200" data-testid="archive-tabs">
    {VISTE.map(([k, label]) => (
      <button key={k} type="button" data-testid={`archive-tab-${k}`} onClick={() => onPick(k)}
        className={`-mb-px whitespace-nowrap rounded-t-lg border px-4 py-2.5 text-sm font-medium transition-colors ${vista === k ? "border-slate-200 border-b-white bg-white text-sky-700" : "border-transparent text-slate-700 hover:text-sky-700"}`}>{label}</button>
    ))}
  </div>
);

const Archivio = ({ direction, year, setYear }) => {
  const [years, setYears] = useState(null);
  useEffect(() => { api.get("/invoices/archive-years", { params: { direction } }).then((r) => setYears(r.data)).catch(() => setYears([])); }, [direction]);
  return (
    <div className="mb-4 flex flex-wrap items-center gap-5 rounded-xl border border-sky-200 bg-sky-100/70 px-6 py-4" data-testid="archive-folders">
      <span className="text-sm font-bold uppercase tracking-wide text-sky-900">Archivio:</span>
      <button type="button" data-testid="archive-year-spostati" onClick={() => setYear("")} className="group flex flex-col items-center">
        {(() => { const I = year === "" ? FolderOpen : Folder; return <I className={`h-11 w-11 fill-sky-300 text-sky-500 ${year === "" ? "scale-110" : ""}`} />; })()}
        <span className={`text-sm font-bold ${year === "" ? "text-sky-800 underline" : "text-slate-900"}`}>Spostati</span>
      </button>
      {years === null ? <span className="text-sm text-slate-600">Caricamento…</span> : years.length === 0 ? <span className="text-sm text-slate-600">Nessun anno precedente in archivio</span> : years.map((y) => {
        const Icon = year === y ? FolderOpen : Folder;
        return (
          <button key={y} type="button" data-testid={`archive-year-${y}`} onClick={() => setYear(y)} className="group flex flex-col items-center">
            <Icon className={`h-11 w-11 fill-amber-400 text-amber-500 transition-transform group-hover:-translate-y-0.5 ${year === y ? "scale-110" : ""}`} />
            <span className={`text-sm font-bold ${year === y ? "text-sky-800 underline" : "text-slate-900"}`}>{y}</span>
          </button>
        );
      })}
    </div>
  );
};

export default function Invoices({ direction = "emessa" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const out = direction === "emessa";
  const base = out ? "/app/fatture" : "/app/ricevute";
  const vista = VISTE.find((v) => v[0] === sp.get("vista") && v[2] === direction)?.[0] || (out ? "inviati" : "ricevuti");
  const storico = vista.startsWith("storico");
  const write = canWrite(user);
  const [items, setItems] = useState(null);
  const [f, setF] = useState(EMPTY_FILTRI);
  const [year, setYear] = useState("");
  const [sel, setSel] = useState([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [sort, setSort] = useState(["data", -1]);
  const pick = (k) => {
    const v = VISTE.find((x) => x[0] === k);
    setYear(""); setSel([]); setPage(1);
    if (v[2] === direction) setSp({ vista: k }); else nav(`${v[2] === "emessa" ? "/app/fatture" : "/app/ricevute"}?vista=${k}`);
  };

  const preset0 = VISTE.find((v) => v[0] === vista)[3];
  const params = { direction, ...filtriParams(f), ...preset0, ...(storico ? (year ? { year } : { archiviata: true }) : {}) };
  const pkey = JSON.stringify(params);
  const load = useCallback(() => { api.get("/invoices", { params: JSON.parse(pkey) }).then((r) => setItems(r.data)); }, [pkey]);

  useEffect(() => {
    const t = setTimeout(() => { load(); setPage(1); setSel([]); }, 250);
    return () => clearTimeout(t);
  }, [load]);

  const nome = (i) => i.controparte?.denominazione || `${i.controparte?.nome || ""} ${i.controparte?.cognome || ""}`;
  const sorted = [...(items || [])].sort((a, b) => sort[1] * (sort[0] === "nome" ? nome(a).localeCompare(nome(b)) : `${a.data}${String(a.progressivo || 0).padStart(8, "0")}`.localeCompare(`${b.data}${String(b.progressivo || 0).padStart(8, "0")}`)));
  const rows = sorted.slice((page - 1) * size, page * size);
  const totale = (items || []).reduce((s, i) => s + (i.tipo === "TD04" ? -1 : 1) * i.totals.totale, 0);
  const printParams = params;
  const allSel = rows.length > 0 && rows.every((i) => sel.includes(i.id));
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const pay = async (i, v) => { try { await api.post(`/invoices/${i.id}/pay`, { pagata: v }); toast.success(v ? "Segnata come saldata" : "Segnata da saldare"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const SortH = ({ k, children }) => <button type="button" className="inline-flex items-center gap-1 font-semibold" data-testid={`sort-${k}`} onClick={() => setSort([k, sort[0] === k ? -sort[1] : 1])}>{children}<ArrowUpDown className="h-3 w-3" /></button>;

  return (
    <div>
      <PageHeader title={out ? "Fatture emesse" : "Fatture ricevute"} subtitle={out ? "Documenti attivi, stato SdI e incassi" : "Fatture passive dai fornitori, importate o ricevute dallo SdI"}>
        <PrintButton testid="print-invoices-button" path="/stampe/fatture.pdf" params={printParams} />
        {canWrite(user) && <ImportXmlDialog onDone={load} />}
        {out && <RicevuteZipDialog />}
        {out && canWrite(user) && (
          <Button asChild data-testid="create-invoice-button" className="bg-sky-600 hover:bg-sky-700">
            <Link to="/app/fatture/nuova"><FilePlus2 className="mr-2 h-4 w-4" />Nuova fattura</Link>
          </Button>
        )}
      </PageHeader>

      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3.5 text-sm text-amber-900" data-testid="conservation-banner">
        Ricordiamo che <b>TUTTE LE FATTURE</b> inviate e ricevute con il nostro sistema, attraverso il canale SDI, <b>SONO CONSERVATE</b> AUTOMATICAMENTE per <b>10 anni + 1</b>
      </div>
      <Tabs vista={vista} onPick={pick} />
      {storico && <Archivio direction={direction} year={year} setYear={setYear} />}

      <Filtri f={f} set={setF} out={out} onReset={() => setF(EMPTY_FILTRI)} />

      {items === null ? (
        <div className="text-sm text-slate-500">Caricamento…</div>
      ) : items.length === 0 ? (
        <Empty text={storico && !year ? "Nessun documento spostato nello storico: scegli un anno dall'archivio" : "Nessun documento in questa sezione"} />
      ) : (
        <>
        {write && <BulkBar ids={sel} clear={() => setSel([])} reload={load} archived={storico && !year} />}
        <Pager total={items.length} page={page} setPage={(n) => { setPage(n); setSel([]); }} size={size} setSize={setSize} />
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table data-testid="invoices-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {write && <TableHead className="w-10"><Checkbox data-testid="invoice-select-all" checked={allSel} onCheckedChange={(v) => setSel(v ? [...new Set([...sel, ...rows.map((i) => i.id)])] : sel.filter((x) => !rows.some((i) => i.id === x)))} /></TableHead>}
                <TableHead>Doc.</TableHead><TableHead><SortH k="data">N°/data doc.</SortH></TableHead><TableHead><SortH k="nome">{out ? "Destinatario" : "Mittente"}</SortH></TableHead>
                <TableHead className="text-right">Importo</TableHead><TableHead>{out ? "Inviata il" : "Ricevuta il"}</TableHead><TableHead>Stato</TableHead>
                <TableHead>PDF · Duplica · Vedi</TableHead><TableHead className="text-center">Saldata</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((i) => (
                <TableRow key={i.id} data-testid={`invoice-row-${i.id}`} className="cursor-pointer transition-colors hover:bg-sky-50/50" onClick={() => nav(`${base}/${i.id}`)}>
                  {write && <TableCell onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`invoice-select-${i.id}`} checked={sel.includes(i.id)} onCheckedChange={() => toggle(i.id)} /></TableCell>}
                  <TableCell className={`whitespace-nowrap text-sm font-medium ${i.tipo === "TD04" ? "text-rose-600" : "text-emerald-700"}`}>{i.tipo === "TD01" ? "Fattura" : <span title={TIPI[i.tipo]}>{TIPI[i.tipo]?.split(" (")[0] || i.tipo} <span className="text-[10px] text-slate-500">{i.tipo}</span></span>}</TableCell>
                  <TableCell className="text-sm"><span className="font-mono font-semibold">{i.numero}</span><br /><span className="text-xs text-slate-500">del {dt(i.data)}</span></TableCell>
                  <TableCell className="max-w-[220px] truncate text-sm">{nome(i)}</TableCell>
                  <TableCell className={`whitespace-nowrap text-right font-mono text-sm font-semibold ${i.tipo === "TD04" ? "text-rose-600" : ""}`}>{i.tipo === "TD04" ? "-" : ""}{eur(i.totals.totale)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{dt(out ? i.sent_at : i.created_at) || "-"}</TableCell>
                  <TableCell><div className="flex flex-col items-start gap-1"><StatusBadge status={i.sdi_status} />{out && i.sdi_status !== "SCARTATA" && <ConservazioneBadge c={i.conservazione} testid={`conservazione-${i.id}`} />}</div></TableCell>
                  <TableCell><RowIcons i={i} out={out} write={write} /></TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    {i.tipo !== "TD04" && <Checkbox data-testid={`invoice-paid-${i.id}`} disabled={!write || (out && ["BOZZA", "SCARTATA"].includes(i.sdi_status))} checked={!!i.pagata} onCheckedChange={(v) => pay(i, !!v)} />}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}><RowMenu i={i} out={out} write={write} base={base} reload={load} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-600">
            <span>{items.length} documenti</span>
            <span data-testid="invoices-total" className="font-mono font-semibold text-slate-900">Totale {eur(totale)}</span>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
